import { confirmSignIn, fetchAuthSession, signIn } from "aws-amplify/auth";

import { configureAmplifyAuth } from "./configureAmplify";
import { decodeJwtPayload } from "./decodeJwt";

/**
 * Passwordless sign-in with an emailed code.
 *
 * Cognito's CUSTOM_AUTH flow, which `stentor-api` backs with the
 * DefineAuthChallenge / CreateAuthChallenge / VerifyAuthChallengeResponse trio.
 * Starting the flow is what causes the email to be sent — there is no separate
 * "send" call.
 *
 * Two things about this flow are easy to get wrong:
 *
 * **It is a code, not a link.** Cognito holds the challenge session server-side
 * and Amplify keeps its handle in memory, so a URL cannot carry it. The user has
 * to bring the code back to the page that started the flow. A true one-click
 * link needs the server to verify the token directly, which is what the
 * get.xapp.ai confirmation endpoint does instead.
 *
 * **The session is short.** Cognito's auth session defaults to about three
 * minutes — far shorter than the code's own 10-minute lifetime. Someone who
 * reads the email slowly will have a valid code and a dead session, so the UI
 * has to offer a restart rather than insisting the code is wrong.
 */

/** Amplify's step name when the pool wants our custom challenge answered. */
const CUSTOM_CHALLENGE_STEP = "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE";

export interface MagicLinkSession {
    accessToken: string;
    /** Epoch seconds, matching what the password flow hands to `onSignIn`. */
    expiresInSeconds: number;
}

export class MagicLinkError extends Error {
    constructor(message: string) {
        super(message);
        // Without this, logs and stack traces read "Error:" and lose the type.
        this.name = "MagicLinkError";
    }
}

/**
 * Starts the flow and triggers the email.
 *
 * @returns true when Cognito is waiting for a code. False means it signed the
 *          user in outright, which should not happen on this pool but is worth
 *          reporting honestly rather than asserting away.
 */
export async function requestSignInCode(email: string): Promise<boolean> {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
        throw new MagicLinkError("Enter your email address first.");
    }

    // Here rather than at app bootstrap: this is the first Amplify call on this
    // route, and configuring at the call site is what keeps a future caller
    // from reintroducing #5459.
    configureAmplifyAuth();

    const { nextStep } = await signIn({
        username: trimmed,
        options: { authFlowType: "CUSTOM_WITHOUT_SRP" },
    });

    return nextStep?.signInStep === CUSTOM_CHALLENGE_STEP;
}

/**
 * Answers the challenge with the emailed code.
 *
 * A wrong code does not throw — Cognito re-issues the challenge, and
 * DefineAuthChallenge allows three attempts before failing the whole sign-in.
 * So "not signed in yet and no error" means "that code was wrong, try again",
 * and it is the caller's job to say so.
 */
export async function submitSignInCode(code: string): Promise<MagicLinkSession | undefined> {
    const trimmed = code.trim();
    if (!trimmed) {
        throw new MagicLinkError("Enter the code from your email.");
    }

    // Same reason as requestSignInCode. A page reloaded between the two steps
    // would otherwise answer the challenge with an unconfigured Amplify.
    configureAmplifyAuth();

    const { isSignedIn } = await confirmSignIn({ challengeResponse: trimmed });
    if (!isSignedIn) {
        return undefined;
    }

    return readSession();
}

/**
 * Shapes the session the same way the password path does, so callers can hand
 * it to the same `onSignIn` without caring which flow produced it.
 */
async function readSession(): Promise<MagicLinkSession> {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const accessToken = session.tokens?.accessToken;

    if (!idToken || !accessToken) {
        throw new MagicLinkError("Signed in, but no session came back. Try again.");
    }

    const { exp } = decodeJwtPayload(idToken.toString());

    return {
        accessToken: accessToken.toString(),
        expiresInSeconds: exp,
    };
}
